import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Button, Card, Col, Form, InputGroup, Row, Spinner } from "react-bootstrap";
import { IconButton, Menu, MenuItem } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { toast } from "../../../components/AppToast";
import CommonDataTable from "../../../components/CommonDataTable";
import CreateCustomerGroup from "./CreateCustomerGroup";
import {
  fetchCustomerGroupList,
  getCustomerGroupById,
} from "../../../services/customer-group/customerGroup";
import "./customerGroup.css";

const STATUS_FILTER_ALL = "All";

const FORM_TITLES = {
  create: "Create Supplier Group",
  edit: "Edit Supplier Group",
  view: "View Supplier Group",
};

export default function CustomerGroupList() {
  const selectedFacility = useSelector((state) => state?.facility?.selectedFacility);
  const locationId = selectedFacility?.value ? String(selectedFacility.value) : undefined;

  const [tableRows, setTableRows] = useState([]);
  const [, setLoading] = useState(true);
  const [formMode, setFormMode] = useState(null);
  const [formEditData, setFormEditData] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuRow, setMenuRow] = useState(null);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(STATUS_FILTER_ALL);

  const formatRow = useCallback((item) => {
    const code =
      item.customerGroupcode ??
      item.code ??
      item.customerGroupCode ??
      "";
    const name = item.customerGroupName ?? item.name ?? "";
    const description = item.customerGroupDescription ?? item.description ?? "";
    const rawStatus = item.status ?? "Active";
    const statusLower = String(rawStatus).trim().toLowerCase();
    const statusLabel = statusLower === "inactive" ? "Inactive" : "Active";
    return {
      id: item.id || item._id,
      customerGroupcode: code,
      customerGroupName: name,
      customerGroupDescription: description,
      status: statusLabel,
      createDate:
        item.createDate ||
        item.createdAt?.split?.("T")?.[0] ||
        item.created_at?.split?.("T")?.[0] ||
        "—",
      createdBy: item.createdBy || item.created_by || "—",
      __original: item,
    };
  }, []);

  // Load customer group list from API (tenant-scoped; optional locationId & status)
  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchCustomerGroupList({
        locationId,
        status: statusFilter === STATUS_FILTER_ALL ? undefined : statusFilter,
      });
      const list = Array.isArray(data) ? data : [];
      setTableRows(list.map(formatRow));
    } catch (err) {
      console.error(err);
      toast.error(typeof err === "string" ? err : "Failed to load customer group records");
      setTableRows([]);
    } finally {
      setLoading(false);
    }
  }, [locationId, statusFilter, formatRow]);

  useEffect(() => {
    if (!formMode) loadList();
  }, [loadList, formMode]);

  const displayRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return tableRows.filter((row) => {
      if (q) {
        const groupCode = String(row.customerGroupcode || "").toLowerCase();
        const groupName = String(row.customerGroupName || "").toLowerCase();
        const description = String(row.customerGroupDescription || "").toLowerCase();
        const createdBy = String(row.createdBy || "").toLowerCase();
        if (!groupCode.includes(q) && !groupName.includes(q) && !description.includes(q) && !createdBy.includes(q)) return false;
      }
      return true;
    });
  }, [tableRows, search]);

  const clearFilters = () => {
    setSearch("");
    setStatusFilter(STATUS_FILTER_ALL);
  };

  const filtersActive = Boolean(search.trim()) || statusFilter !== STATUS_FILTER_ALL;

  const handleOpenMenu = (e, row) => {
    e.stopPropagation();
    setMenuAnchorEl(e.currentTarget);
    setMenuRow(row);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
    setMenuRow(null);
  };

  const closeForm = () => {
    setFormMode(null);
    setFormEditData(null);
    setFormLoading(false);
  };

  const loadDetailAndOpenForm = async (row, mode) => {
    if (!row?.id) return;
    handleCloseMenu();
    setFormMode(mode);
    setFormEditData(null);
    setFormLoading(true);
    try {
      const detail = await getCustomerGroupById(row.id);
      setFormEditData(detail && typeof detail === "object" ? detail : row.__original);
    } catch (err) {
      console.error(err);
      toast.error(typeof err === "string" ? err : "Failed to load supplier group");
      setFormEditData(row.__original);
    } finally {
      setFormLoading(false);
    }
  };

  const openCreateForm = () => {
    setFormEditData(null);
    setFormMode("create");
    setFormLoading(false);
  };

  const handleView = (row) => loadDetailAndOpenForm(row, "view");
  const handleEdit = (row) => loadDetailAndOpenForm(row, "edit");

  const actionColumn = useMemo(
    () => ({
      field: "__actions",
      headerName: "",
      width: 72,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <IconButton size="small" onClick={(e) => handleOpenMenu(e, params.row)} aria-label="row actions">
          <MoreVertIcon fontSize="small" />
        </IconButton>
      ),
    }),
    []
  );

  const rawColumns = useMemo(
    () => [
      {
        field: "customerGroupcode",
        headerName: "Supplier Group Code",
        width: 180,
        renderCell: (params) => (
          <div style={{ fontWeight: 500 }}>{params.value}</div>
        ),
      },
      {
        field: "customerGroupName",
        headerName: "Supplier Group Name",
        width: 200,
      },
      {
        field: "customerGroupDescription",
        headerName: "Supplier Group Description",
        width: 250,
      },
      {
        field: "status",
        headerName: "Status",
        width: 100,
        renderCell: (params) => (
          <span
            style={{
              color: params.value === "Active" ? "green" : "red",
              fontWeight: 500,
            }}
          >
            {params.value}
          </span>
        ),
      },
      {
        field: "createDate",
        headerName: "Create Date",
        width: 140,
      },
      {
        field: "createdBy",
        headerName: "Created By",
        width: 150,
      },
    ],
    []
  );

  const allPossibleColumns = useMemo(() => [actionColumn, ...rawColumns], [actionColumn, rawColumns]);
  const initialVisible = useMemo(() => allPossibleColumns.map((c) => c.field), [allPossibleColumns]);

  if (formMode) {
    const groupLabel =
      formEditData?.customerGroupcode ||
      formEditData?.code ||
      formEditData?.customerGroupCode ||
      "";
    const breadcrumbLabel =
      formMode === "create"
        ? "New Supplier Group"
        : formMode === "edit"
          ? `Edit Supplier Group — ${groupLabel}`
          : `View Supplier Group — ${groupLabel}`;

    return (
      <Card className="customer-group-list-card border-0 shadow-sm">
          <div className="card-header bg-secondary text-white py-2">
            <h4 className="mb-0 h6 fw-semibold">
              {FORM_TITLES[formMode]}
              {groupLabel && formMode !== "create" ? ` — ${groupLabel}` : ""}
            </h4>
          </div>
          <nav className="px-3 py-2 border-bottom bg-light" aria-label="breadcrumb">
            <ol className="breadcrumb mb-0">
              <li className="breadcrumb-item">
                <button type="button" className="btn btn-link p-0 text-decoration-none text-muted" onClick={closeForm}>
                  Supplier Groups
                </button>
              </li>
              <li className="breadcrumb-item active fw-semibold text-primary" aria-current="page">
                {breadcrumbLabel}
              </li>
            </ol>
          </nav>
          <Card.Body>
            {formLoading ? (
              <div className="text-center py-5 text-secondary">
                <Spinner animation="border" size="sm" className="me-2" role="status" />
                Loading supplier group details…
              </div>
            ) : (
              <CreateCustomerGroup
                editData={formMode !== "create" ? formEditData : null}
                readOnly={formMode === "view"}
                onClose={closeForm}
                onSuccess={() => {
                  closeForm();
                  loadList();
                }}
              />
            )}
          </Card.Body>
      </Card>
    );
  }

  return (
    <>
      <Card className="customer-group-list-card border-0 shadow-sm mb-3">
        <Card.Header className="bg-white py-3 border-bottom">
          <Row className="g-2 align-items-center">
            <Col xs={12} md="auto">
              <h5 className="mb-0 text-primary fw-semibold">Supplier Groups</h5>
            </Col>
            <Col xs={12} md={6} lg={3}>
              <Form.Group className="mb-0">
                <Form.Select
                  size="sm"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  aria-label="Filter by status"
                >
                  <option value={STATUS_FILTER_ALL}>All statuses</option>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xs={12} lg={4} className="ms-lg-auto">
              <InputGroup size="sm">
                <Form.Control
                  placeholder="Search by supplier group code, name, or description..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                {filtersActive && (
                  <Button variant="outline-secondary" onClick={clearFilters}>
                    Clear
                  </Button>
                )}
              </InputGroup>
            </Col>
          </Row>
        </Card.Header>
        <Card.Body className="p-0">
          <CommonDataTable
            allPossibleColumns={allPossibleColumns}
            initialVisible={initialVisible}
            initialRows={displayRows}
            height={Math.max(360, Math.min(displayRows.length, 12) * 52 + 120)}
            rowsPerPageOptions={[10, 25, 50, 100]}
            getRowId={(row) => row.id}
            forInventory
            onDataRefresh={() => {}}
            showCreateButton={true}
            createButtonText="Create Supplier Group"
            createButtonIcon="bi bi-plus-lg"
            onCreateClick={openCreateForm}
          />
        </Card.Body>
      </Card>

      <Menu open={Boolean(menuAnchorEl)} anchorEl={menuAnchorEl} onClose={handleCloseMenu}>
        <MenuItem onClick={() => handleView(menuRow)}>
          <i className="bi bi-eye me-2" /> View
        </MenuItem>
        <MenuItem onClick={() => handleEdit(menuRow)}>
          <i className="bi bi-pencil me-2" /> Edit
        </MenuItem>
      </Menu>
    </>
  );
}